import {useEffect, useState} from 'react'
import BigNumber from 'bignumber.js'
import {DAO} from '~/config'
import {getBep20Contract, getCakeContract, getGoldenPearlContrat} from '~/utils/contractHelpers'
import {chainTokens} from '~/config/constants/tokens'
import {BIG_ZERO} from '~/utils/bigNumber'
import {getProvider} from '~/utils/providers'
import useRefresh from './useRefresh'
import useActiveWeb3React from './useActiveWeb3React'
import {Address, getAddress} from "viem";
import {useAccount, useBalance} from "wagmi";

type UseTokenBalanceState = {
  balance: BigNumber
  fetchStatus: FetchStatus
}

export enum FetchStatus {
  NOT_FETCHED = 'not-fetched',
  SUCCESS = 'success',
  FAILED = 'failed',
}

const useTokenBalance = (tokenAddress: string) => {
  const {NOT_FETCHED, SUCCESS, FAILED} = FetchStatus
  const [balanceState, setBalanceState] = useState<UseTokenBalanceState>({
    balance: BIG_ZERO,
    fetchStatus: NOT_FETCHED,
  })
  const {account, chainId} = useActiveWeb3React()
  const {fastRefresh} = useRefresh()

  useEffect(() => {
    const fetchBalance = async () => {
      const contract = getBep20Contract(chainId, getAddress(tokenAddress), getProvider(chainId))
      try {
        const res = await contract.read.balanceOf([account])
        setBalanceState({balance: new BigNumber(res.toString()), fetchStatus: SUCCESS})
      } catch (e) {
        console.error(e)
        setBalanceState((prev) => ({
          ...prev,
          fetchStatus: FAILED,
        }))
      }
    }

    if (account) {
      fetchBalance()
    }
  }, [account, tokenAddress, fastRefresh, SUCCESS, FAILED, chainId])

  return balanceState
}

export const useGetDAORewardTokenBalance = () => {
  const {slowRefresh} = useRefresh()
  const [daoBalance, setDaoBalance] = useState<BigNumber>()
  const {chainId} = useActiveWeb3React()

  useEffect(() => {
    async function fetchTotalSupply() {
      const lumContract = getCakeContract(chainId)
      const supply = await lumContract.read.balanceOf([DAO as Address])
      setDaoBalance(new BigNumber(supply.toString()))
    }

    fetchTotalSupply()
  }, [slowRefresh, chainId])

  return daoBalance
}

export const useTotalSupply = () => {
  const {slowRefresh} = useRefresh()
  const [totalSupply, setTotalSupply] = useState<BigNumber>()
  const {chainId} = useActiveWeb3React()

  useEffect(() => {
    async function fetchTotalSupply() {
      const cakeContract = getCakeContract(chainId)
      const supply = await cakeContract.read.totalSupply()
      setTotalSupply(new BigNumber(supply.toString()))
    }

    fetchTotalSupply()
  }, [slowRefresh, chainId])

  return totalSupply
}

export const useGoldenTotalSupply = () => {
  const {slowRefresh} = useRefresh()
  const [totalSupply, setGoldenTotalSupply] = useState<BigNumber>()
  const {chainId} = useActiveWeb3React()

  useEffect(() => {
    async function fetchGoldenTotalSupply() {
      const cakeContract = getGoldenPearlContrat(chainId);
      const supply = await cakeContract.read.totalSupply()
      setGoldenTotalSupply(new BigNumber(supply.toString()))
    }

    fetchGoldenTotalSupply()
  }, [slowRefresh, chainId])

  return totalSupply
}

export const useBurnedBalance = (tokenAddress: string) => {
  const [balance, setBalance] = useState(BIG_ZERO)
  const {slowRefresh} = useRefresh()
  const {chainId} = useActiveWeb3React()

  useEffect(() => {
    const fetchBalance = async () => {
      const contract = getBep20Contract(chainId, getAddress(tokenAddress))
      const res = await contract.read.balanceOf(['0x000000000000000000000000000000000000dEaD' as Address])
      setBalance(new BigNumber(res.toString()))
    }

    fetchBalance()
  }, [tokenAddress, slowRefresh, chainId])

  return balance
}

export const useGetBnbBalance = () => {
  // const [fetchStatus, setFetchStatus] = useState(FetchStatus.NOT_FETCHED)
  // const [balance, setBalance] = useState(ethers.BigNumber.from(0))
  const {chainId} = useActiveWeb3React()
  // const { lastUpdated, setLastUpdated } = useLastUpdated()

  // useEffect(() => {
  //   const fetchBalance = async () => {
  //     try {
  //       const walletBalance = await getProvider(chainId).getBalance(account)
  //       setBalance(walletBalance)
  //       setFetchStatus(FetchStatus.SUCCESS)
  //     } catch {
  //       setFetchStatus(FetchStatus.FAILED)
  //     }
  //   }

  //   if (account) {
  //     fetchBalance()
  //   }
  // }, [account, lastUpdated, setBalance, setFetchStatus, chainId])

  // return { balance, fetchStatus, refresh: setLastUpdated }
  const {address: account} = useAccount()
  const {status, refetch, data} = useBalance({
    chainId,
    address: account,
    watch: true,
    enabled: !!account,
  })
  return {balance: data?.value ? BigInt(data.value) : 0n, fetchStatus: status, refresh: refetch}
}

export const useGetCakeBalance = () => {
  const {chainId} = useActiveWeb3React()
  const {balance, fetchStatus} = useTokenBalance(chainTokens.lum.address[chainId])
  return {balance, fetchStatus}
}

export const useGetSMRBalance = () => {
  const {chainId} = useActiveWeb3React()
  const {balance, fetchStatus} = useTokenBalance(chainTokens.eth.address[chainId])
  return {balance, fetchStatus}
}

export default useTokenBalance
